import React from 'react';
import { Progress, List, Typography, Space } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';

const { Text } = Typography;

export interface PasswordValidation {
    isValid: boolean;
    score: number;
    errors: string[];
    suggestions: string[];
}

interface PasswordStrengthValidatorProps {
    password: string;
    onValidationChange?: (validation: PasswordValidation) => void;
    showRequirements?: boolean;
}

/**
 * Password Strength Validator Component
 * Matches backend validation requirements exactly
 * Provides real-time feedback to users
 */
const PasswordStrengthValidator: React.FC<PasswordStrengthValidatorProps> = ({
    password,
    onValidationChange,
    showRequirements = true
}) => {
    // Common weak passwords (subset of backend list)
    const commonPasswords = [
        'password', '123456', '123456789', 'qwerty', 'abc123', 'password123',
        'admin', 'letmein', 'welcome', 'monkey', '1234567890', 'password1'
    ];

    const validatePassword = (pwd: string): PasswordValidation => {
        const errors: string[] = [];
        const suggestions: string[] = [];
        let score = 0;

        // Length check
        if (pwd.length < 8) {
            errors.push('Password must be at least 8 characters long');
            suggestions.push('Add more characters to reach the 8 character minimum');
        } else {
            score += 20;
        }

        if (pwd.length > 128) {
            errors.push('Password must not exceed 128 characters');
        }

        // Uppercase letter check
        if (!/[A-Z]/.test(pwd)) {
            errors.push('Password must contain at least one uppercase letter (A-Z)');
            suggestions.push('Add an uppercase letter');
        } else {
            score += 20;
        }

        // Lowercase letter check
        if (!/[a-z]/.test(pwd)) {
            errors.push('Password must contain at least one lowercase letter (a-z)');
            suggestions.push('Add a lowercase letter');
        } else {
            score += 20;
        }

        // Digit check
        if (!/\d/.test(pwd)) {
            errors.push('Password must contain at least one digit (0-9)');
            suggestions.push('Add a number');
        } else {
            score += 20;
        }

        // Special character check
        if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd)) {
            errors.push('Password must contain at least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?)');
            suggestions.push('Add a special character like !@#$%^&*');
        } else {
            score += 20;
        }

        // Common password check
        const lowerPwd = pwd.toLowerCase();
        if (commonPasswords.includes(lowerPwd)) {
            errors.push('Password is too common and easily guessable');
            suggestions.push('Choose a more unique password');
            score = Math.max(0, score - 30);
        }

        // Sequential pattern checks
        if (/123|abc|qwe|asd|zxc/i.test(pwd)) {
            errors.push('Password contains sequential patterns');
            suggestions.push('Avoid sequential characters like 123, abc, or qwe');
            score = Math.max(0, score - 15);
        }

        // Repeated character check
        if (/(.)\1{2,}/.test(pwd)) {
            errors.push('Password contains too many repeated characters');
            suggestions.push('Avoid repeating the same character multiple times');
            score = Math.max(0, score - 10);
        }

        // Bonus points for length
        if (pwd.length >= 12) score += 10;
        if (pwd.length >= 16) score += 10;

        // Bonus for character variety
        const uniqueChars = new Set(pwd.toLowerCase()).size;
        if (uniqueChars >= 8) score += 10;

        return {
            isValid: errors.length === 0,
            score: Math.min(100, score),
            errors,
            suggestions
        };
    };

    const validation = React.useMemo(() => validatePassword(password), [password]);

    // Notify parent component of validation changes
    React.useEffect(() => {
        if (onValidationChange) {
            onValidationChange(validation);
        }
    }, [validation, onValidationChange]);

    const getProgressColor = () => {
        if (validation.score < 40) return '#ff4d4f';
        if (validation.score < 70) return '#faad14';
        return '#52c41a';
    };

    const getStrengthText = () => {
        if (validation.score < 30) return 'Very Weak';
        if (validation.score < 50) return 'Weak';
        if (validation.score < 70) return 'Fair';
        if (validation.score < 90) return 'Good';
        return 'Excellent';
    };

    const requirements = [
        { text: 'At least 8 characters', met: password.length >= 8 },
        { text: 'One uppercase letter (A-Z)', met: /[A-Z]/.test(password) },
        { text: 'One lowercase letter (a-z)', met: /[a-z]/.test(password) },
        { text: 'One number (0-9)', met: /\d/.test(password) },
        { text: 'One special character (!@#$%^&*)', met: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password) }
    ];

    if (!password) {
        return null;
    }

    return (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div>
                <Space align="center">
                    <Text strong>Password Strength:</Text>
                    <Text style={{ color: getProgressColor(), fontWeight: 'bold' }}>
                        {getStrengthText()}
                    </Text>
                </Space>
                <Progress
                    percent={validation.score}
                    strokeColor={getProgressColor()}
                    showInfo={false}
                    size="small"
                />
            </div>

            {showRequirements && (
                <div>
                    <Text strong style={{ fontSize: '12px' }}>Requirements:</Text>
                    <List
                        size="small"
                        dataSource={requirements}
                        renderItem={(item) => (
                            <List.Item style={{ padding: '2px 0', border: 'none' }}>
                                <Space align="center">
                                    {item.met ? (
                                        <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '12px' }} />
                                    ) : (
                                        <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '12px' }} />
                                    )}
                                    <Text 
                                        style={{ 
                                            fontSize: '12px',
                                            color: item.met ? '#52c41a' : '#8c8c8c',
                                            textDecoration: item.met ? 'line-through' : 'none'
                                        }}
                                    >
                                        {item.text}
                                    </Text>
                                </Space>
                            </List.Item>
                        )}
                    />
                </div>
            )}

            {validation.errors.length > 0 && (
                <div>
                    <Text type="danger" style={{ fontSize: '12px' }}>
                        Issues to fix:
                    </Text>
                    <List
                        size="small"
                        dataSource={validation.errors}
                        renderItem={(error) => (
                            <List.Item style={{ padding: '2px 0', border: 'none' }}>
                                <Text type="danger" style={{ fontSize: '11px' }}>
                                    • {error}
                                </Text>
                            </List.Item>
                        )}
                    />
                </div>
            )}

            {validation.suggestions.length > 0 && validation.score < 80 && (
                <div>
                    <Text type="warning" style={{ fontSize: '12px' }}>
                        Suggestions:
                    </Text>
                    <List
                        size="small"
                        dataSource={validation.suggestions.slice(0, 2)} // Show max 2 suggestions
                        renderItem={(suggestion) => (
                            <List.Item style={{ padding: '2px 0', border: 'none' }}>
                                <Text type="warning" style={{ fontSize: '11px' }}>
                                    • {suggestion}
                                </Text>
                            </List.Item>
                        )}
                    />
                </div>
            )}

            {validation.isValid && validation.score >= 70 && (
                <div>
                    <Space align="center">
                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                        <Text type="success" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                            Password meets all security requirements!
                        </Text>
                    </Space>
                </div>
            )}
        </Space>
    );
};

export default PasswordStrengthValidator;
